import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface ProductSummary {
  product_id: string;
  title: string;
  sku: string;
  vendor: string;
  photo_url: string | null;
  category: string | null;
  variant_count: number;
  in_stock: boolean;
  lowest_price: number | null;
  total_quantity: number;
}

export interface Filters {
  searchTerm: string;
  category: string;
  vendor: string;
  stockStatus: 'all' | 'in_stock' | 'out_of_stock';
  minPrice: number | null;
  maxPrice: number | null;
}

export interface PaginationState {
  page: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export const useProviderProducts = (providerId: string | null) => {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [allVendors, setAllVendors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>({
    searchTerm: '',
    category: '',
    vendor: '',
    stockStatus: 'all',
    minPrice: null,
    maxPrice: null,
  });

  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    itemsPerPage: 24,
    totalItems: 0,
    totalPages: 0,
  });

  // Fetch unique categories and vendors for filters
  const loadFilterOptions = useCallback(async () => {
    if (!providerId) return;

    try {
      const { data: catData } = await supabase
        .from('product_descriptions')
        .select('category')
        .eq('provider_id', providerId)
        .not('category', 'is', null);

      const categories = Array.from(new Set((catData || []).map((r: any) => r.category).filter(Boolean)));
      setAllCategories(categories.sort());

      const { data: vendorData } = await supabase
        .from('product_descriptions')
        .select('vendor')
        .eq('provider_id', providerId)
        .not('vendor', 'is', null);

      const vendors = Array.from(new Set((vendorData || []).map((r: any) => r.vendor).filter(Boolean)));
      setAllVendors(vendors.sort());
    } catch (e: any) {
      console.error('Error loading filter options:', e);
    }
  }, [providerId]);

  // Fetch products with server-side filters and pagination
  const loadProducts = useCallback(async () => {
    if (!providerId) {
      setProducts([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const offset = (pagination.page - 1) * pagination.itemsPerPage;

      const { data, error: rpcError } = await supabase.rpc('get_provider_catalog', {
        p_provider_id: providerId,
        p_limit: pagination.itemsPerPage,
        p_offset: offset,
        p_search: filters.searchTerm || null,
        p_category: filters.category || null,
        p_vendor: filters.vendor || null,
        p_stock_status: filters.stockStatus,
        p_min_price: filters.minPrice,
        p_max_price: filters.maxPrice,
      });

      if (rpcError) throw rpcError;

      const rows = data || [];
      const totalItems = rows.length > 0 ? Number(rows[0].total_count) : 0;
      const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);

      const productList: ProductSummary[] = rows.map((row: any) => ({
        product_id: row.product_id,
        title: row.title || 'Untitled Product',
        sku: row.sku || 'N/A',
        vendor: row.vendor || 'Unknown',
        photo_url: row.photo_url,
        category: row.category,
        variant_count: Number(row.variant_count),
        in_stock: row.in_stock,
        lowest_price: row.lowest_price !== null ? Number(row.lowest_price) : null,
        total_quantity: Number(row.total_quantity),
      }));

      setProducts(productList);
      setPagination(prev => ({
        ...prev,
        totalItems,
        totalPages,
      }));

    } catch (e: any) {
      console.error('Error loading products:', e);
      setError(e.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [providerId, filters, pagination.page, pagination.itemsPerPage]);

  // Load filter options on provider change
  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Reload products when filters or pagination change
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Reset to page 1 when filters change
  const updateFilters = (newFilters: Partial<Filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      searchTerm: '',
      category: '',
      vendor: '',
      stockStatus: 'all',
      minPrice: null,
      maxPrice: null,
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const setPage = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const setItemsPerPage = (itemsPerPage: number) => {
    setPagination(prev => ({ ...prev, itemsPerPage, page: 1 }));
  };

  return {
    products,
    loading,
    error,
    filters,
    updateFilters,
    resetFilters,
    pagination,
    setPage,
    setItemsPerPage,
    allCategories,
    allVendors,
    refresh: loadProducts,
  };
};
